import axios from 'axios';

// URL du backend - utilise la variable d'environnement ; defaut = API VPS impartiale (jamais Render)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.impartiale.com';
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  uploadAvatar: (formData) => api.post('/auth/me/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Clients
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getOne: (id) => api.get(`/clients/${id}`),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Affaires
export const affairesAPI = {
  getAll: (statut) => api.get('/affaires', { params: { statut } }),
  getOne: (id) => api.get(`/affaires/${id}`),
  get: (id) => api.get(`/affaires/${id}`),
  create: (data) => api.post('/affaires', data),
  update: (id, data) => api.put(`/affaires/${id}`, data),
  delete: (id) => api.delete(`/affaires/${id}`),
};

// Conversations
export const conversationsAPI = {
  getAll: () => api.get('/conversations'),
  get: (id) => api.get(`/conversations/${id}`),
  getMessages: (id) => api.get(`/conversations/${id}/messages`),
  create: (data) => api.post('/conversations', data),
  update: (id, data) => api.put(`/conversations/${id}`, data),
  delete: (id) => api.delete(`/conversations/${id}`),
};

// Chat
export const chatAPI = {
  send: (data) => api.post('/chat', data),
};

// Echeances
export const echeancesAPI = {
  getAll: () => api.get('/echeances'),
  create: (data) => api.post('/echeances', data),
  update: (id, data) => api.put(`/echeances/${id}`, data),
  complete: (id) => api.put(`/echeances/${id}/complete`),
  delete: (id) => api.delete(`/echeances/${id}`),
};

// Audiences
export const audiencesAPI = {
  getAll: () => api.get('/audiences'),
  create: (data) => api.post('/audiences', data),
  update: (id, data) => api.put(`/audiences/${id}`, data),
  delete: (id) => api.delete(`/audiences/${id}`),
};

// Notes
export const notesAPI = {
  getAll: (affaireId) => api.get('/notes', { params: { affaire_id: affaireId } }),
  create: (data) => api.post('/notes', data),
  update: (id, data) => api.put(`/notes/${id}`, data),
  delete: (id) => api.delete(`/notes/${id}`),
};

// Documents
export const documentsAPI = {
  getAll: (affaireId) => api.get('/documents', { params: { affaire_id: affaireId } }),
  create: (data) => api.post('/documents', data),
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/documents/${id}`),
  // Authenticated, ownership-scoped binary fetch (used for preview + download).
  // Replaces the former public /uploads URL.
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
};

// Stats
export const statsAPI = {
  get: () => api.get('/stats'),
};

// Taches
export const tachesAPI = {
  getAll: (affaireId, statut) => api.get('/taches', { params: { affaire_id: affaireId, statut } }),
  getOne: (id) => api.get(`/taches/${id}`),
  create: (data) => api.post('/taches', data),
  update: (id, data) => api.put(`/taches/${id}`, data),
  delete: (id) => api.delete(`/taches/${id}`),
  updateStatus: (id, statut) => api.put(`/taches/${id}/status?statut=${statut}`),
  // Timer functions
  startTimer: (id) => api.post(`/taches/${id}/timer/start`),
  stopTimer: (id, description) => api.post(`/taches/${id}/timer/stop`, { description }),
  getTimerStatus: (id) => api.get(`/taches/${id}/timer/status`),
  // Get time entries linked to task
  getTemps: (id) => api.get(`/taches/${id}/temps`),
};

// Diligences (tarifs)
export const diligencesAPI = {
  getAll: () => api.get('/diligences'),
  create: (data) => api.post('/diligences', data),
  update: (id, data) => api.put(`/diligences/${id}`, data),
  delete: (id) => api.delete(`/diligences/${id}`),
};

// Temps (feuilles de temps)
export const tempsAPI = {
  getAll: (filters = {}) => api.get('/temps', { params: filters }),
  getByAffaire: (affaireId) => api.get('/temps', { params: { affaire_id: affaireId } }),
  create: (data) => api.post('/temps', data),
  update: (id, data) => api.put(`/temps/${id}`, data),
  delete: (id) => api.delete(`/temps/${id}`),
  marquerFacture: (id) => api.put(`/temps/${id}/facturer`),
  marquerFactures: (ids) => {
    const formData = new FormData();
    formData.append('temps_ids', ids.join(','));
    return api.post('/temps/marquer-factures', formData);
  },
  getFacturation: (affaireId) => api.get(`/temps/facturation/${affaireId}`),
};

// Factures (PDF generation)
export const facturesAPI = {
  generatePDF: (options = {}) => {
    const formData = new FormData();
    if (options.affaire_id) formData.append('affaire_id', options.affaire_id);
    if (options.temps_ids) formData.append('temps_ids', options.temps_ids.join(','));
    if (options.date_start) formData.append('date_start', options.date_start);
    if (options.date_end) formData.append('date_end', options.date_end);
    return api.post('/factures/generate-pdf', formData, { responseType: 'blob' });
  },
  generateReport: (options = {}) => {
    const formData = new FormData();
    if (options.affaire_id) formData.append('affaire_id', options.affaire_id);
    if (options.date_start) formData.append('date_start', options.date_start);
    if (options.date_end) formData.append('date_end', options.date_end);
    if (options.include_non_billable) formData.append('include_non_billable', 'true');
    return api.post('/factures/generate-report', formData, { responseType: 'blob' });
  }
};

// Exports PDF - génération de documents
export const exportsAPI = {
  // Facture d'honoraires
  facture: (affaireId, includeNonBillable = false) => 
    api.get(`/exports/facture/${affaireId}`, { 
      params: { include_non_billable: includeNonBillable },
      responseType: 'blob' 
    }),
  
  // Devis / Proposition commerciale
  devis: (affaireId, options = {}) => 
    api.get(`/exports/devis/${affaireId}`, { 
      params: { 
        montant_estime: options.montant,
        description: options.description 
      },
      responseType: 'blob' 
    }),
  
  // Compte rendu de consultation
  consultation: (affaireId) => 
    api.get(`/exports/consultation/${affaireId}`, { responseType: 'blob' }),
  
  // Rapport de temps
  rapportTemps: (affaireId, dateDebut, dateFin) => 
    api.get(`/exports/rapport-temps/${affaireId}`, { 
      params: { date_debut: dateDebut, date_fin: dateFin },
      responseType: 'blob' 
    }),
};

// Predictions IA
export const predictionsAPI = {
  generate: (affaireId) => api.post(`/affaires/${affaireId}/prediction`),
  getAll: (affaireId) => api.get(`/affaires/${affaireId}/predictions`),
};

// Recherche
export const rechercheAPI = {
  // Availability of the official (PISTE-backed) legal search — no secret exposure
  getStatus: () => api.get('/recherche/status'),
  // === JURISPRUDENCE JUDICIAIRE (Judilibre) ===
  judilibre: (query, filters = {}) => api.get('/recherche/judilibre', {
    params: { query, ...filters } 
  }),
  courCassation: (query, filters = {}) => api.get('/recherche/cour-cassation', {
    params: { query, ...filters }
  }),
  coursAppel: (query, filters = {}) => api.get('/recherche/cours-appel', {
    params: { query, ...filters }
  }),
  tribunauxJudiciaires: (query, filters = {}) => api.get('/recherche/tribunaux-judiciaires', {
    params: { query, ...filters }
  }),
  tribunauxCommerce: (query, filters = {}) => api.get('/recherche/tribunaux-commerce', {
    params: { query, ...filters }
  }),
  prudhommes: (query, filters = {}) => api.get('/recherche/prudhommes', {
    params: { query, ...filters }
  }),
  getDecision: (id) => api.get(`/recherche/judilibre/${id}`),
  getJuridictions: () => api.get('/recherche/judilibre/juridictions'),
  
  // === LÉGISLATION (Légifrance) ===
  legifrance: (query, filters = {}) => api.get('/recherche/legifrance', { 
    params: { query, ...filters } 
  }),
  getText: (id, articleId) => api.get(`/recherche/legifrance/${id}`, { 
    params: articleId ? { article_id: articleId } : {}
  }),
  
  // === JURISPRUDENCE ADMINISTRATIVE ===
  conseilEtat: (query, filters = {}) => api.get('/recherche/conseil-etat', {
    params: { query, ...filters }
  }),
  
  // === JURISPRUDENCE CONSTITUTIONNELLE ===
  conseilConstitutionnel: (query, filters = {}) => api.get('/recherche/conseil-constitutionnel', {
    params: { query, ...filters }
  }),
  
  // === DROIT EUROPÉEN ===
  eurlex: (query, filters = {}) => api.get('/recherche/eurlex', {
    params: { query, ...filters }
  }),
  cjue: (query, filters = {}) => api.get('/recherche/cjue', {
    params: { query, ...filters }
  }),
  cedh: (query, filters = {}) => api.get('/recherche/cedh', {
    params: { query, ...filters }
  }),
  
  // === DOCTRINE ===
  doctrine: (query) => api.get('/recherche/doctrine', { params: { query } }),
  
  // === RECHERCHE COMBINÉE ===
  toutesSources: (query, sources) => api.get('/recherche/toutes-sources', {
    params: { query, sources } 
  }),
  getSourcesDisponibles: () => api.get('/recherche/sources-disponibles'),
};

// Roadmap & System Health
export const roadmapAPI = {
  // Features
  getFeatures: () => api.get('/roadmap/features'),
  toggleVerified: (featureId) => api.put(`/roadmap/features/${featureId}/verify`),
  
  // Health checks
  checkDatabase: () => api.get('/roadmap/health/database'),
  checkApis: () => api.get('/roadmap/health/apis'),
  fullHealthCheck: () => api.get('/roadmap/health/full'),
};

// Tokens / Crédits
export const tokensAPI = {
  getBalance: () => api.get('/tokens/balance'),
  consume: (data) => api.post('/tokens/consume', data),
  getHistory: (limit = 20) => api.get('/tokens/history', { params: { limit } }),
};

// Stripe / Paiements
export const stripeAPI = {
  getPlans: () => api.get('/stripe/plans'),
  createCheckout: (data) => api.post('/stripe/create-checkout', data),
  createPortal: (returnUrl) => api.post('/stripe/create-portal', { return_url: returnUrl }),
  getSubscription: () => api.get('/stripe/subscription'),
  testConnection: () => api.get('/stripe/test-connection'),
  verifySession: (sessionId) => api.get(`/stripe/verify-session/${sessionId}`),
};

// Orchestrateur IA Multi-Provider
export const orchestratorAPI = {
  search: (data) => api.post('/search/orchestrated', data),
  searchStream: (data) => {
    // SSE streaming pour recherches longues
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      message: data.message,
      tier: data.tier || 'standard',
      ...(data.affaire_id && { affaire_id: data.affaire_id })
    });
    
    return new EventSource(
      `${api.defaults.baseURL}/search/orchestrated/stream?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },
  getTiers: () => api.get('/tiers'),
  getProvidersStatus: () => api.get('/providers/status'),
};

// Client Portal (Espace Client) - gestion par l'avocat
export const clientPortalAPI = {
  // Liste des clients avec leur statut portail
  getClientsStatus: () => api.get('/client-portal/clients-status'),
  
  // Inviter un client à rejoindre le portail (envoie un email)
  inviteClient: (clientId) => api.post('/client-portal/invite', { client_id: clientId }),
  
  // Désactiver l'accès portail d'un client
  disableClient: (clientId) => api.post(`/client-portal/disable/${clientId}`),
  
  // Réactiver l'accès portail (renvoie une invitation)
  reactivateClient: (clientId) => api.post('/client-portal/invite', { client_id: clientId }),
  
  // Créer un accès direct avec mot de passe (sans email)
  setupDirectAccess: (clientId, password) => api.post('/client-portal/setup-direct', { 
    client_id: clientId, 
    password 
  }),
};

export default api;
